import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { KeyRound, Plus, Copy, Trash2, Check, Loader2, ShieldAlert, Terminal, SearchCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Copy-paste quick-start so a web developer can wire up travcations.com fast.
const API_SNIPPET = `# Read-only Travcations CRM API — send your key in the X-API-Key header.
BASE="${BACKEND_URL}/api/public"
KEY="YOUR_API_KEY"

# ── Fetch content ──────────────────────────────────────────────
curl -H "X-API-Key: $KEY" "$BASE/packages"            # published packages
curl -H "X-API-Key: $KEY" "$BASE/packages/<slug>"     # one package
curl -H "X-API-Key: $KEY" "$BASE/destinations"        # destinations
curl -H "X-API-Key: $KEY" "$BASE/testimonials"        # testimonials
curl -H "X-API-Key: $KEY" "$BASE/offers"              # active offers
curl -H "X-API-Key: $KEY" "$BASE/company"             # company info
curl -H "X-API-Key: $KEY" "$BASE/seo/<slug>"          # per-page SEO

# ── Submit a website lead ──────────────────────────────────────
curl -X POST "$BASE/leads" -H "X-API-Key: $KEY" -H "Content-Type: application/json" \\
  -d '{"name":"Jane","email":"jane@example.com","phone":"+91 98765 43210","destination":"Goa","message":"Honeymoon in Dec"}'

# ── Submit a contact-form message ──────────────────────────────
curl -X POST "$BASE/contact" -H "X-API-Key: $KEY" -H "Content-Type: application/json" \\
  -d '{"name":"Jane","email":"jane@example.com","message":"Please call me back"}'`;

const QuickStart = () => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(API_SNIPPET); setCopied(true); toast.success('Snippet copied'); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error('Copy failed'); }
  };
  return (
    <div className="bg-white/90 rounded-2xl border border-slate-200/60 p-6 shadow-sm" data-testid="api-quickstart">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2">
          <Terminal className="h-5 w-5 text-primary-600" />
          <h3 className="text-lg font-bold text-slate-800">Quick start</h3>
        </div>
        <Button size="sm" variant="outline" onClick={copy} className="shrink-0" data-testid="api-quickstart-copy">
          {copied ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />} Copy
        </Button>
      </div>
      <p className="text-sm text-slate-500 mb-4">Hand this to your web developer to wire up travcations.com. Base URL <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">{BACKEND_URL}/api/public</code></p>
      <pre className="text-[11px] leading-relaxed bg-slate-900 text-slate-100 rounded-xl p-4 overflow-x-auto font-mono" data-testid="api-quickstart-snippet">{API_SNIPPET}</pre>
    </div>
  );
};

const fmt = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

// One key row — module-level to keep nested .map() out of the parent JSX.
const ApiKeyRow = ({ k, onRevoke, revoking, onToggleSkip, togglingSkip }) => (
  <div className="flex items-center justify-between gap-3 py-3" data-testid={`api-key-row-${k.keyId}`}>
    <div className="min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-semibold text-slate-800 truncate">{k.label}</span>
        {k.revoked ? (
          <span className="text-[10px] uppercase font-semibold text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded">Revoked</span>
        ) : (
          <span className="text-[10px] uppercase font-semibold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">Active</span>
        )}
        {!k.revoked && k.skipRateLimit ? (
          <span className="text-[10px] uppercase font-semibold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded" data-testid={`api-key-nolimit-badge-${k.keyId}`}>No rate limit</span>
        ) : null}
      </div>
      <p className="text-xs text-slate-400 mt-0.5 font-mono">{k.prefix}••••••</p>
      <p className="text-[11px] text-slate-400 mt-0.5">Created {fmt(k.createdAt)} · Last used {fmt(k.lastUsedAt)}</p>
    </div>
    {!k.revoked ? (
      <div className="flex items-center gap-2 shrink-0">
        <Button size="sm" variant="outline" className="border-indigo-200 text-indigo-600 hover:bg-indigo-50" disabled={togglingSkip} onClick={() => onToggleSkip(k)} data-testid={`api-key-skiplimit-${k.keyId}`}>
          {k.skipRateLimit ? 'Enable rate limit' : 'Bypass rate limit'}
        </Button>
        <Button size="sm" variant="outline" className="border-rose-200 text-rose-600 hover:bg-rose-50" disabled={revoking} onClick={() => onRevoke(k)} data-testid={`api-key-revoke-${k.keyId}`}>
          <Trash2 className="h-3.5 w-3.5 mr-1" /> Revoke
        </Button>
      </div>
    ) : null}
  </div>
);

const ApiKeysSettings = () => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [label, setLabel] = useState('');
  const [creating, setCreating] = useState(false);
  const [revokingId, setRevokingId] = useState('');
  const [skipId, setSkipId] = useState('');
  const [newKey, setNewKey] = useState(null); // plaintext shown once
  const [copied, setCopied] = useState(false);
  const [checkVal, setCheckVal] = useState('');
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState(null);

  const load = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/api-keys`, { withCredentials: true });
      setKeys(res.data.items || []);
    } catch {
      toast.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!label.trim()) { toast.error('Enter a label for the key'); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/api-keys`, { label: label.trim() }, { withCredentials: true });
      setNewKey(res.data);
      setLabel('');
      setCopied(false);
      await load();
      toast.success('API key generated');
    } catch {
      toast.error('Failed to generate key');
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (k) => {
    setRevokingId(k.keyId);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/api-keys/${k.keyId}/revoke`, {}, { withCredentials: true });
      await load();
      toast.success('Key revoked');
    } catch {
      toast.error('Failed to revoke key');
    } finally {
      setRevokingId('');
    }
  };

  const toggleSkip = async (k) => {
    setSkipId(k.keyId);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/api-keys/${k.keyId}/skip-rate-limit`, { enabled: !k.skipRateLimit }, { withCredentials: true });
      await load();
      toast.success(k.skipRateLimit ? 'Rate limit re-enabled' : 'Rate limit bypass enabled');
    } catch {
      toast.error('Failed to update rate-limit setting');
    } finally {
      setSkipId('');
    }
  };

  const copyKey = async () => {
    try { await navigator.clipboard.writeText(newKey.apiKey); setCopied(true); toast.success('Key copied'); }
    catch { toast.error('Copy failed'); }
  };

  const checkKey = async () => {
    if (!checkVal.trim()) { toast.error('Paste a key to check'); return; }
    setChecking(true);
    setCheckResult(null);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/api-keys/verify`, { apiKey: checkVal.trim() }, { withCredentials: true });
      setCheckResult(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not check key');
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="api-keys-settings">
      <div className="bg-white/90 rounded-2xl border border-slate-200/60 p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-1">
          <KeyRound className="h-5 w-5 text-primary-600" />
          <h3 className="text-lg font-bold text-slate-800">Website API Keys</h3>
        </div>
        <p className="text-sm text-slate-500 mb-5">Read-only keys for your marketing website to fetch packages, destinations, testimonials &amp; offers and submit leads/contacts. Send the key in the <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">X-API-Key</code> header.</p>

        {/* one-time plaintext reveal */}
        {newKey ? (
          <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50 p-4 mb-5" data-testid="api-key-new-reveal">
            <p className="text-sm font-semibold text-emerald-900 mb-1">Copy your new key now — it won't be shown again.</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs bg-white border border-emerald-200 rounded-lg px-3 py-2 font-mono break-all" data-testid="api-key-new-value">{newKey.apiKey}</code>
              <Button size="sm" onClick={copyKey} className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0" data-testid="api-key-copy">
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <button type="button" className="text-[11px] text-emerald-700 underline mt-2" onClick={() => setNewKey(null)}>Done, hide key</button>
          </div>
        ) : null}

        {/* generate */}
        <div className="flex items-end gap-3 mb-2">
          <div className="flex-1">
            <Label className="text-sm font-semibold text-slate-700 mb-2 block">New key label</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Marketing Website (prod)" className="h-11 rounded-xl" data-testid="api-key-label-input" onKeyDown={(e) => e.key === 'Enter' && create()} />
          </div>
          <Button onClick={create} disabled={creating} className="h-11 bg-primary-700 hover:bg-primary-600 text-white" data-testid="api-key-generate-btn">
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" /> Generate</>}
          </Button>
        </div>
      </div>

      {/* verify / diagnose a key */}
      <div className="bg-white/90 rounded-2xl border border-slate-200/60 p-6 shadow-sm" data-testid="api-key-verify-card">
        <div className="flex items-center gap-2 mb-1">
          <SearchCheck className="h-5 w-5 text-primary-600" />
          <h3 className="text-lg font-bold text-slate-800">Verify a key</h3>
        </div>
        <p className="text-sm text-slate-500 mb-4">Paste the key your website / WordPress is sending in <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">X-API-Key</code> to confirm it's <strong>valid</strong> and not revoked. Nothing is stored — this just checks it against your keys.</p>
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <Label className="text-sm font-semibold text-slate-700 mb-2 block">API key to check</Label>
            <Input value={checkVal} onChange={(e) => { setCheckVal(e.target.value); setCheckResult(null); }} placeholder="tvk_live_…" className="h-11 rounded-xl font-mono" data-testid="api-key-verify-input" onKeyDown={(e) => e.key === 'Enter' && checkKey()} />
          </div>
          <Button onClick={checkKey} disabled={checking} className="h-11 bg-primary-700 hover:bg-primary-600 text-white" data-testid="api-key-verify-btn">
            {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : <><SearchCheck className="h-4 w-4 mr-1" /> Check</>}
          </Button>
        </div>
        {checkResult ? (
          <div className="mt-4 rounded-xl border p-4 text-sm" data-testid="api-key-verify-result"
            style={{ borderColor: checkResult.status === 'valid' ? '#a7f3d0' : '#fecaca', background: checkResult.status === 'valid' ? '#ecfdf5' : '#fef2f2' }}>
            {checkResult.status === 'valid' ? (
              <div>
                <p className="font-semibold text-emerald-800" data-testid="api-key-verify-status">✓ Valid &amp; active — “{checkResult.label}”</p>
                <p className="text-emerald-700 mt-1">Rate-limit bypass: <strong>{checkResult.skipRateLimit ? 'ON' : 'OFF'}</strong>{!checkResult.skipRateLimit ? ' — turn it ON below for your WordPress server so lead pushes aren\u2019t throttled.' : ''}</p>
                <p className="text-emerald-700 mt-1">Last used {fmt(checkResult.lastUsedAt)}</p>
              </div>
            ) : checkResult.status === 'revoked' ? (
              <p className="font-semibold text-rose-700" data-testid="api-key-verify-status">✗ This key is <strong>REVOKED</strong> (“{checkResult.label}”). Generate a new key above and update WordPress.</p>
            ) : (
              <div>
                <p className="font-semibold text-rose-700" data-testid="api-key-verify-status">✗ Unknown key — it doesn't match any key here.</p>
                <p className="text-rose-700 mt-1">{checkResult.gateOpen ? 'No active keys exist yet, so the public API is currently OPEN to everyone. Generate a key and put it in WordPress.' : 'It was likely created on a different environment or deleted. Generate a fresh key above and update WordPress.'}</p>
              </div>
            )}
          </div>
        ) : null}
      </div>

      {/* list */}
      <div className="bg-white/90 rounded-2xl border border-slate-200/60 p-6 shadow-sm">
        <h4 className="text-sm font-bold text-slate-700 mb-2">Your keys</h4>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : keys.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-slate-400 py-6">
            <ShieldAlert className="h-4 w-4" /> No keys yet. The public API stays open until you create your first key, then it locks to valid keys only.
          </div>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="api-keys-list">
            {keys.map((k) => (
              <ApiKeyRow key={k.keyId} k={k} onRevoke={revoke} revoking={revokingId === k.keyId} onToggleSkip={toggleSkip} togglingSkip={skipId === k.keyId} />
            ))}
          </div>
        )}
      </div>

      <QuickStart />
    </div>
  );
};

export default ApiKeysSettings;
